"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Flex, Text } from "@radix-ui/themes";

interface Props {
  isVisible: boolean;
}

// Narrow, animated, absolutely positioned banner. Top on desktop, bottom on mobile.
const BidderFairWarningBanner = ({ isVisible }: Props) => {
  // Tunable values
  const DESKTOP_TOP_START = 100; // px when page is at top
  const DESKTOP_TOP_END = 10; // px when scrolled past threshold
  const SCROLL_INTERPOLATE_END = 120; // px scroll distance to reach DESKTOP_TOP_END

  const [isDesktop, setIsDesktop] = useState(false);
  const [desktopTop, setDesktopTop] = useState<number>(DESKTOP_TOP_START);
  const tickingRef = useRef(false);

  // Track breakpoint (Tailwind lg: min-width 1024px)
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches);
    };
    setIsDesktop(mql.matches);
    // addEventListener is supported broadly; fall back if needed
    if (mql.addEventListener) {
      mql.addEventListener("change", handleChange);
    } else {
      // @ts-ignore older Safari
      mql.addListener(handleChange);
    }
    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener("change", handleChange);
      } else {
        // @ts-ignore older Safari
        mql.removeListener(handleChange);
      }
    };
  }, []);

  // Interpolate top on scroll for desktop only
  useEffect(() => {
    if (!isDesktop) return;

    const updateTop = () => {
      const y = window.scrollY || 0;
      const clamped = Math.max(0, Math.min(y, SCROLL_INTERPOLATE_END));
      const t = clamped / SCROLL_INTERPOLATE_END;
      const nextTop = DESKTOP_TOP_START + (DESKTOP_TOP_END - DESKTOP_TOP_START) * t;
      setDesktopTop(nextTop);
    };

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        updateTop();
        tickingRef.current = false;
      });
    };

    // Set initial position
    updateTop();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [isDesktop]);

  return (
    <Flex
      align="center"
      justify="center"
      // Positioning: absolute container that spans width; inner content is narrow
      className={cn(
        "pointer-events-none select-none",
        // Absolute positioning with responsive placement
        "fixed left-0 right-0 z-40",
        // Top on desktop (lg+), bottom on mobile
        "bottom-6 lg:bottom-auto",
        // Transition for enter/exit
        "duration-300",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 lg:-translate-y-3"
      )}
      style={isDesktop ? { top: desktopTop } : undefined}
    >
      <Flex
        align="center"
        justify="center"
        className={cn(
          "w-content max-w-[520px]",
          "rounded-full border bg-amber-100 px-4 py-2 shadow-sm",
          "relative",
          "pointer-events-auto"
        )}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-amber-500 animate-ring-pulse-strong"
        />
        <Text className="relative z-10 text-sm font-medium text-amber-900">
          Fair Warning: Closing Soon
        </Text>
      </Flex>
    </Flex>
  );
};

export default BidderFairWarningBanner;


